import React, { useEffect, useState } from 'react';
import { Download, ImageIcon, Loader2, RefreshCw, Repeat } from 'lucide-react';
import { AspectFrame } from '@/components/AspectFrame';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useI18n } from '@/context/LanguageContext';
import { fileUrl } from '@/lib/api';

export const PreviewPanel = ({
  orientation,
  reference,
  generation,
  generating,
  onDownload,
  onVariation,
  onReset,
  downloading,
}) => {
  const { t } = useI18n();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!generating) {
      setTick(0);
      return undefined;
    }
    const id = setInterval(() => setTick((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [generating]);

  const STATUS_TEXTS = [
    t('preview.status1'),
    t('preview.status2'),
    t('preview.status3'),
    t('preview.status4'),
    t('preview.status5'),
  ];

  const statusText = STATUS_TEXTS[Math.min(Math.floor(tick / 5), STATUS_TEXTS.length - 1)];
  const progress = Math.min(95, 6 + tick * 3.2);
  const done = generation?.status === 'done' && generation?.result_url;
  const failed = generation?.status === 'failed';

  return (
    <div className="panel overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b border-[hsl(var(--border-soft))] px-4 py-3">
        <h3 className="font-display text-sm font-semibold">{t('preview.title')}</h3>
        <span className="font-mono-alt text-[11px] text-[hsl(var(--text-muted))]">
          {orientation === 'portrait' ? '1080 x 1920' : '1920 x 1080'}
        </span>
      </div>

      <div className="p-4">
        <AspectFrame orientation={orientation} testId="live-preview-frame">
          {generating ? (
            <div data-testid="loading-state" className="relative flex h-full w-full flex-col items-center justify-center gap-3 overflow-hidden bg-[hsl(var(--surface-2))] shimmer px-6">
              <Loader2 className="h-7 w-7 animate-spin text-[hsl(var(--brand-red))]" />
              <p data-testid="generation-status-text" className="text-center text-sm font-medium text-white">
                {statusText}
              </p>
              <p className="font-mono-alt text-[11px] text-[hsl(var(--text-muted))]">
                {tick} {t('common.seconds')}
              </p>
            </div>
          ) : done ? (
            <img
              src={fileUrl(generation.result_url)}
              alt={t('preview.resultAlt')}
              data-testid="result-image"
              className="h-full w-full object-cover"
            />
          ) : failed ? (
            <div data-testid="error-state" className="flex h-full w-full flex-col items-center justify-center gap-2 px-6 text-center">
              <p className="text-sm font-semibold text-[hsl(var(--brand-red))]">{t('preview.failedTitle')}</p>
              <p className="text-xs text-[hsl(var(--text-muted))]">{generation.error}</p>
              <p className="text-xs text-[hsl(var(--text-muted))]">{t('preview.refunded')}</p>
            </div>
          ) : reference ? (
            <img
              src={fileUrl(reference.url)}
              alt={t('preview.refAlt')}
              data-testid="preview-reference-image"
              className="h-full w-full object-cover opacity-60"
            />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center gap-2 px-6 text-center">
              <ImageIcon className="h-7 w-7 text-[hsl(var(--text-muted))]" />
              <p className="text-xs text-[hsl(var(--text-muted))]">{t('preview.emptyHint')}</p>
            </div>
          )}
        </AspectFrame>

        {generating && (
          <div className="mt-4">
            <Progress value={progress} data-testid="generation-progress" className="h-1.5 bg-[hsl(var(--surface-2))]" />
            <p className="mt-2 text-center text-xs text-[hsl(var(--text-muted))]">{t('preview.dontClose')}</p>
          </div>
        )}

        {done && (
          <div className="mt-4 space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-[hsl(var(--text-muted))]">
              <span className="font-mono-alt">
                {generation.width} x {generation.height} px
              </span>
              <span>
                {generation.model_label} - {generation.duration}
                {t('common.seconds')}
              </span>
            </div>
            <Button
              onClick={onDownload}
              disabled={downloading}
              data-testid="download-png-button"
              className="h-11 w-full bg-[hsl(var(--brand-red))] font-semibold text-white hover:bg-[hsl(var(--brand-red-2))]"
            >
              {downloading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
              {t('preview.downloadPng')}
            </Button>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={onVariation}
                data-testid="generate-variation-button"
                className="h-10 flex-1 border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface-2))] text-sm hover:bg-[hsl(var(--surface))]"
              >
                <Repeat className="mr-2 h-4 w-4" /> {t('preview.variation')}
              </Button>
              <Button
                variant="secondary"
                onClick={onReset}
                data-testid="new-thumbnail-button"
                className="h-10 flex-1 border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface-2))] text-sm hover:bg-[hsl(var(--surface))]"
              >
                <RefreshCw className="mr-2 h-4 w-4" /> {t('preview.newCover')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
